import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { client, type Application, type Department, type ServiceStatus } from '../lib/api';
import { AppStatusChip, ServiceStatusChip } from '../components/StatusChip';
import { AppMonogram, Section } from '../components/Page';
import { useAuth } from '../lib/AuthContext';
import { tokens } from '../theme';

const AUDIENCES = [
  {
    eyebrow: 'For staff',
    title: 'Use the tools',
    body: 'Open the apps IT Lab has built and see what each one does.',
    cta: 'Browse applications',
    to: '/apps',
  },
  {
    eyebrow: 'For department leads',
    title: 'Get something built',
    body: 'Describe a problem, book a scoping session, and track the request until it ships.',
    cta: 'Request an app',
    to: '/request',
  },
  {
    eyebrow: 'For everyone',
    title: 'Know what’s up',
    body: 'Live health and incidents for the portal and every IT Lab application.',
    cta: 'View status',
    to: '/status',
  },
];

const cardLinkSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  p: 4,
  border: `1px solid ${tokens.border}`,
  borderRadius: 2,
  textDecoration: 'none',
  color: 'text.primary',
  transition: 'border-color 120ms ease',
  '&:hover': { borderColor: 'primary.main' },
  '&:hover .cta': { textDecoration: 'underline' },
} as const;

export function HomePage() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalog, status] = await Promise.all([client.catalog(), client.status()]);
        if (cancelled) return;
        setDepartments(catalog.departments);
        setApplications(catalog.applications);
        setServices(status.services);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const overall = services.some((s) => s.status === 'outage')
    ? 'outage'
    : services.some((s) => s.status === 'degraded')
      ? 'degraded'
      : services.some((s) => s.status === 'maintenance')
        ? 'maintenance'
        : 'operational';

  const firstName = user?.name?.split(' ')[0];
  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? 'Department';

  const stats = [
    { label: 'Applications', value: applications.length },
    { label: 'Departments served', value: departments.length },
    { label: 'Services monitored', value: services.length },
  ];

  return (
    <>
      {error && (
        <Section py={3}>
          <Alert severity="error">{error}</Alert>
        </Section>
      )}

      {/* Hero */}
      <Section py={{ xs: 7, md: 11 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 6, md: 8 }} alignItems="center">
          <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
            {firstName && (
              <Typography fontWeight={600} color="primary.main">
                Welcome back, {firstName}
              </Typography>
            )}
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 60 }, letterSpacing: '-0.035em' }}>
              Software that keeps AST running.
            </Typography>
            <Typography sx={{ fontSize: { xs: 18, md: 20 }, color: 'text.secondary', maxWidth: 520 }}>
              IT Lab builds the internal tools every department relies on — from slide decks to shop-floor
              yield. Find an app, check it’s up, or ask us for a new one.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={3} alignItems={{ sm: 'center' }}>
              <Button
                component={RouterLink}
                to="/apps"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Browse applications
              </Button>
              <Link component={RouterLink} to="/status" fontSize={17}>
                Check service status
              </Link>
            </Stack>
          </Stack>

          <Box
            sx={{
              flex: 1,
              width: '100%',
              minWidth: 0,
              aspectRatio: { md: '5 / 4' },
              borderRadius: 2,
              bgcolor: tokens.tint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 3, md: 5 },
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 440,
                bgcolor: '#fff',
                border: '1px solid #D0D9E4',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                sx={{ px: 1.75, py: 1.5, borderBottom: `1px solid ${tokens.divider}`, bgcolor: tokens.subtle }}
              >
                {[0, 1, 2].map((i) => (
                  <Box key={i} aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tokens.border }} />
                ))}
                <Box flex={1} />
                <ServiceStatusChip status={overall} />
              </Stack>
              <Stack spacing={1.5} sx={{ p: 2.5 }}>
                {loading
                  ? [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={66} />)
                  : applications.slice(0, 4).map((app) => (
                      <Stack
                        key={app.id}
                        component={RouterLink}
                        to={`/apps/${app.slug}`}
                        direction="row"
                        alignItems="center"
                        gap={1.75}
                        sx={{
                          p: 1.5,
                          border: `1px solid ${tokens.divider}`,
                          borderRadius: 1.5,
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <AppMonogram name={app.name} size="sm" />
                        <Box flex={1} minWidth={0}>
                          <Typography fontWeight={600} fontSize={15} color={tokens.ink} noWrap>
                            {app.name}
                          </Typography>
                          <Typography fontSize={13} color={tokens.text3} noWrap>
                            {deptName(app.department_id)}
                          </Typography>
                        </Box>
                        <AppStatusChip status={app.status} />
                      </Stack>
                    ))}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Section>

      {/* Statement */}
      <Section band="subtle" id="about">
        <Stack spacing={7}>
          <Typography
            component="p"
            sx={{
              fontSize: { xs: 28, md: 40 },
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              color: tokens.ink,
              maxWidth: 960,
            }}
          >
            We build the tools AST teams count on, move fast, and replace spreadsheets with software that
            just works.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={6}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Typography variant="h2" fontSize={22}>
                What we’ve built
              </Typography>
              <Typography color="text.secondary" maxWidth={480}>
                Each application is owned by IT Lab end to end: we scope it with the department, build it,
                and keep it running. Every app lists its features, stack and live health.
              </Typography>
              <Link component={RouterLink} to="/apps">
                See every application →
              </Link>
            </Stack>
            <Box
              component="dl"
              sx={{ flex: 1.2, m: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 3 }}
            >
              {stats.map((s) => (
                <Box key={s.label} sx={{ borderTop: `3px solid ${tokens.blue}`, pt: 2 }}>
                  <Typography component="dt" fontSize={14} color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{ m: 0, fontSize: { xs: 36, md: 48 }, fontWeight: 800, letterSpacing: '-0.03em', color: tokens.ink }}
                  >
                    {loading ? '–' : s.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Stack>
      </Section>

      {/* Audiences */}
      <Section>
        <Stack spacing={5}>
          <Stack spacing={1}>
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 } }}>
              A place for every team
            </Typography>
            <Typography fontSize={18} color="text.secondary">
              Start from what you need today.
            </Typography>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {AUDIENCES.map((a) => (
              <Box key={a.title} component={RouterLink} to={a.to} sx={cardLinkSx}>
                <Typography fontSize={14} fontWeight={600} color="primary.main">
                  {a.eyebrow}
                </Typography>
                <Typography variant="h3" fontSize={22}>
                  {a.title}
                </Typography>
                <Typography color="text.secondary">{a.body}</Typography>
                <Typography className="cta" sx={{ mt: 'auto', pt: 1.5, fontWeight: 600, color: 'primary.main' }}>
                  {a.cta} →
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      </Section>

      {/* Applications */}
      <Box sx={{ borderTop: `1px solid ${tokens.divider}` }}>
        <Section>
          <Stack spacing={5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={2} flexWrap="wrap">
              <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 } }}>
                Our applications
              </Typography>
              <Link component={RouterLink} to="/apps">
                Discover all applications →
              </Link>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
              {loading
                ? [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={300} />)
                : applications.map((app) => (
                    <Box
                      key={app.id}
                      component={RouterLink}
                      to={`/apps/${app.slug}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover h3': { color: 'primary.main', textDecoration: 'underline' },
                      }}
                    >
                      <AppMonogram name={app.name} />
                      <Stack spacing={0.75} px={0.5}>
                        <Box
                          component="span"
                          sx={{
                            alignSelf: 'flex-start',
                            px: 1.25,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: tokens.strip,
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#344054',
                          }}
                        >
                          {deptName(app.department_id)}
                        </Box>
                        <Typography variant="h3" fontSize={20}>
                          {app.name}
                        </Typography>
                        <Typography color="text.secondary">{app.tagline}</Typography>
                      </Stack>
                    </Box>
                  ))}
            </Box>
          </Stack>
        </Section>
      </Box>

      {/* CTA band */}
      <Section band="brand" py={{ xs: 7, md: 10 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={4} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack spacing={1.5} maxWidth={600}>
            <Typography variant="h2" color="#fff" sx={{ fontSize: { xs: 28, md: 36 } }}>
              Have an idea for a tool?
            </Typography>
            <Typography fontSize={18} color="#D6E6F5">
              If your team is stuck in spreadsheets or manual steps, tell us. We’ll book a scoping session on
              a date that suits you.
            </Typography>
          </Stack>
          <Button
            component={RouterLink}
            to="/request"
            size="large"
            sx={{
              bgcolor: '#fff',
              color: tokens.blueDeep,
              fontWeight: 700,
              alignSelf: { xs: 'flex-start', md: 'center' },
              textDecoration: 'none',
              '&:hover': { bgcolor: '#EEF4FA' },
            }}
          >
            Request an app
          </Button>
        </Stack>
      </Section>
    </>
  );
}
